/**
 * <image-cycler>
 * Crossfades between its `.fp-cycler__img` children on a timer. The active
 * slide carries `is-active` (opacity 1); the rest fade out via CSS. Used by
 * the featured-product section's image cycler.
 *
 * Attributes:
 *   data-interval  ms between slides (default 5000)
 *
 * No-ops with fewer than 2 slides and pauses under prefers-reduced-motion or
 * when the tab is hidden.
 */
class ImageCycler extends HTMLElement {
  /** @type {ReturnType<typeof setInterval> | null} */
  #timer = null;
  /** @type {HTMLElement[]} */
  #slides = [];
  /** @type {number} */
  #index = 0;
  /** @type {number} */
  #interval = 5000;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('.fp-cycler__img'));
    if (this.#slides.length < 2) return;

    this.#interval = Math.max(1500, parseInt(this.dataset.interval ?? '5000', 10) || 5000);
    this.#index = Math.max(0, this.#slides.findIndex((slide) => slide.classList.contains('is-active')));

    this.#start();
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  disconnectedCallback() {
    this.#stop();
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #start() {
    if (this.#timer || this.#slides.length < 2 || this.#prefersReducedMotion()) return;
    this.#timer = setInterval(this.#advance, this.#interval);
  }

  #stop() {
    if (this.#timer) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  #advance = () => {
    this.#slides[this.#index]?.classList.remove('is-active');
    this.#index = (this.#index + 1) % this.#slides.length;
    this.#slides[this.#index]?.classList.add('is-active');
  };

  #onVisibility = () => {
    if (document.hidden) this.#stop();
    else this.#start();
  };
}

if (!customElements.get('image-cycler')) {
  customElements.define('image-cycler', ImageCycler);
}
